<template>
  <tab-item-template>
    <template #toolbar-items>
      <v-btn icon @click="openCreateDialog" v-if="isEditable">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-btn icon @click="toggleTableExpand(true)">
        <v-icon>mdi-arrow-expand-vertical</v-icon>
      </v-btn>
      <v-btn icon @click="toggleTableExpand(false)">
        <v-icon>mdi-arrow-collapse-vertical</v-icon>
      </v-btn>
    </template>

    <draggable-el-table
        @end="handleDragEnd">
      <el-table
          ref="propertyTable"
          :class="{'no-padding': true, isEditable, 'hide-first-table-header-row': isCategory2nd('common')}"
          :height="tabItemBounds.height"
          border
          row-key="target.id"
          :data="dataPropertyDefinitionsView"
          :cell-class-name="cellClassName">

        <!-- ターゲット側 -->
        <el-table-column>
          <template #header>{{ dictionary.name }} - {{ dataComponentModel.name }}</template>
          <el-table-column
              align="center"
              width="25"
              v-if="isEditable"
              key="handle">
            <v-icon class="handle">mdi-menu</v-icon>
          </el-table-column>
          <el-table-column
              width="50"
              align="center"
              label="追番"
              prop="target.index"
              key="target.index">
            <template #default="{row}">
              <el-tooltip v-if="row.target.isPrimaryKey" placement="bottom" content="主キー">
                <span>{{ row.target.index }}</span>
              </el-tooltip>
              <span v-else>{{ row.target.index }}</span>
            </template>
          </el-table-column>
          <el-table-column
              align="center"
              label="主キー"
              key="target.isPrimaryKey"
              width="80"
              v-if="isEditable">
            <template #default="{row}">
              <table-inline>
                <el-checkbox
                    v-model="row.target.isPrimaryKey"
                    @change="handleChange(row.target)">
                </el-checkbox>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              min-width="25"
              label="名称"
              key="target.name">
            <template #default="{row}">
              <table-inline>
                <v-text-field
                    v-model="row.target.name"
                    :readonly="!isEditable"
                    @change="handleChange(row.target)">
                  <template #append-outer v-if="!isCategory2nd('common')">
                    <error-popover
                        :errors="row.errors"
                        style="padding-right: 0.3rem;">
                    </error-popover>
                  </template>
                </v-text-field>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              width="120"
              align="center"
              label="データ型"
              key="target.dataType">
            <template #default="{row}">
              <table-inline>
                <model-select
                    v-model="row.target.dataType"
                    :items="dataTypesForSelect"
                    item-text="text"
                    item-value="value"
                    :return-object="false"
                    :readonly="!isEditable"
                    @change="handleChange(row.target)">
                </model-select>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              width="50"
              align="center"
              label="必須"
              key="target.isRequired">
            <template #default="{row}" v-if="isEditable">
              <table-inline>
                <el-checkbox
                    v-model="row.target.isRequired"
                    @change="handleChange(row.target)">
                </el-checkbox>
              </table-inline>
            </template>
            <template #default="{row}" v-else>
              <readonly-checkbox :value="row.target.isRequired"></readonly-checkbox>
            </template>
          </el-table-column>
          <el-table-column
              class-name="description"
              label="説明"
              key="target.description"
              min-width="50">
            <template #default="{row}">
              <table-inline>
                <description-input
                    v-model="row.target.description"
                    :readonly="!isEditable"
                    @change="handleChange(row.target)">
                </description-input>
              </table-inline>
            </template>
          </el-table-column>
        </el-table-column>

        <!-- 共通辞書側 -->
        <el-table-column v-if="!isCategory2nd('common')">
          <template #header>{{ selectedDictionary.name }} - {{ selectedDataComponentModel.name }}</template>
          <el-table-column
              width="50"
              align="center"
              label="追番"
              prop="common.index"
              key="common.index">
            <template #default="{row}">
              <el-tooltip v-if="row.common.isPrimaryKey" placement="bottom" content="主キー">
                <span>{{ row.common.index }}</span>
              </el-tooltip>
              <span v-else>{{ row.common.index }}</span>
            </template>
          </el-table-column>
          <el-table-column
              min-width="25"
              label="名称"
              key="common.name">
            <template #default="{row}">
              <table-inline>
                <model-select
                    :value="row.commonItemId"
                    :items="commonDataPropertyDefinitionView"
                    item-value="itemId"
                    :item-sort="(dpdA, dpdB) => dpdA.index - dpdB.index"
                    :groups="dataRelationModelTreeFlatten"
                    :expanded-groups="commonDataComponentModelExpanded"
                    :clearable="isEditable"
                    :readonly="!isEditable"
                    @change="handleChangeCommonDataPropertyDefinition(row.target.id, ...arguments)"
                    @change-expanded-group="commonDataComponentModelExpanded[$event.group.id] = $event.isActive"
                    default-expand-all
                    taller>
                  <template #item="{item, on, attrs}">
                    <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover data-component-model-menu-item">
                      <v-col class="name">
                        <menu-inline>
                          <text-field-like
                              label="名称"
                              :value="item.name"
                              :value-hds-id="item.dataPropertyDefinitionId">
                          </text-field-like>
                        </menu-inline>
                      </v-col>
                      <v-col>
                        <menu-inline>
                          <text-field-like
                              label="説明"
                              :value="item.description"
                              placeholder="未設定"
                              multi-line>
                          </text-field-like>
                        </menu-inline>
                      </v-col>
                    </v-list-item>
                  </template>
                </model-select>
              </table-inline>
            </template>
          </el-table-column>
          <el-table-column
              width="110"
              align="center"
              label="型"
              key="common.dataType">
            <template #default="{row}">
              {{ dataTypes[row.common.dataType] || row.common.dataType }}
            </template>
          </el-table-column>
          <el-table-column
              width="50"
              align="center"
              label="必須"
              key="common.isRequired">
            <template #default="{row}">
              <readonly-checkbox
                  v-if="row.common.id"
                  :value="row.common.isRequired">
              </readonly-checkbox>
            </template>
          </el-table-column>
        </el-table-column>

        <!-- ボタン -->
        <el-table-column
            v-if="isEditable"
            width="40"
            align="center"
            key="operations">
          <template #default="{row}">
            <icon-btns>
              <icon-btn
                  v-if="isEditable"
                  type="delete"
                  @click="openDeleteDialog(row.target)">
              </icon-btn>
            </icon-btns>
          </template>
        </el-table-column>

        <!-- 展開 -->
        <el-table-column
            type="expand">
          <template #default="{row}">
            <v-row>
              <v-col cols="6">
                <v-text-field
                    label="指定可能な値の例"
                    placeholder="値は空です"
                    v-model="row.target.possibleValues"
                    hide-details
                    dense
                    :readonly="!isEditable"
                    @change="handleChange(row.target)">
                </v-text-field>
              </v-col>
            </v-row>
          </template>
        </el-table-column>
      </el-table>
    </draggable-el-table>


    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
           データ項目定義作成ダイアログ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <simple-dialog
        v-model="dialogCreate"
        title-text="データ項目定義作成"
        @close="closeCreateDialog">
      <v-row>
        <v-col cols="12">
          <v-text-field
              autofocus
              v-model="dialogCreate.name"
              label="データ項目定義名"
              hint="後から変更が可能です"
              :rules="[v => !!v || 'データ項目定義名が必要です']"/>
        </v-col>
        <v-col cols="12">
          <v-textarea
              v-model="dialogCreate.description"
              label="説明"
              messages="複数行入力可"
              rows="1"
              auto-grow/>
        </v-col>
      </v-row>
    </simple-dialog>


    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
           データ項目定義削除ダイアログ
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <simple-dialog
        v-model="dialogDelete"
        title-text="データ項目定義削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のデータ項目定義を削除します。本当によろしいですか？"
        @close="closeDeleteDialog">
      <v-row>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.name"
              label="データ項目定義名">
          </text-field-like>
        </v-col>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.description"
              label="説明"
              placeholder="未設定"
              multi-line>
          </text-field-like>
        </v-col>
      </v-row>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import DraggableElTable from "@/components/atoms/DraggableElTable.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import DescriptionInput from "@/components/molecules/DescriptionInput.vue";
import {mapActions, mapGetters, mapMutations} from "vuex";
import mutationDataPropertyDefinitionAdd from "@/gql/DataComponentModel/DataPropertyDefinitionAdd.gql";
import mutationDataPropertyDefinitionRemove from "@/gql/DataComponentModel/DataPropertyDefinitionRemove.gql";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";
import {DATA_PROPERTY_DEFINITION_DATA_TYPES} from "@/constants";
import {generateTemporaryID} from "@/store/modules/hds";
import ErrorPopover from "@/components/molecules/ErrorPopover.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import MenuInline from "@/components/atoms/MenuInline.vue";
import DataTranslationMapAddGql from "@/gql/DataComponentModel/DataTranslationMapAdd.gql";
import Swal from "sweetalert2";

export default {
  name: 'DataPropertyDefinitionList',
  components: {
    MenuInline,
    TextFieldLike,
    IconBtn,
    IconBtns,
    ErrorPopover,
    ReadonlyCheckbox,
    ModelSelect, TabItemTemplate, DescriptionInput, TableInline, SimpleDialog, DraggableElTable},
  mixins: [PathInfoMixin],
  props: {
    dictionary: {
      type: Object,
      required: true
    },
    dataComponentModel: {
      type: Object,
      required: true
    },
    selectedDictionary: {
      type: Object,
      required: false
    },
    selectedDataComponentModel: {
      type: Object,
      required: false
    }
  },
  inject: [
    "tabItemBounds"
  ],
  data() {
    return {
      dataPropertyDefinitionsView: [],
      commonDataComponentModelExpanded: {},
      dialogCreate: false,
      dialogDelete: false,
      dataTypesForSelect: Object.entries(DATA_PROPERTY_DEFINITION_DATA_TYPES).map(([value, text]) => Object.freeze({text, value})),
      dataTypes: DATA_PROPERTY_DEFINITION_DATA_TYPES,
    };
  },
  computed: {
    ...mapGetters({
      entities: 'hds/entities',
    }),
    dataPropertyDefinitions() {
      return this.entities.dataPropertyDefinition
          .find({dataComponentModelId: this.dataComponentModel.id})
          .sort((a, b) => a.index - b.index);
    },

    /**
     * ルートとなるデータ変換マップを取得します。
     * ルートとなるデータ変換マップとは、個別辞書と共通辞書のデータ構成モデルを関連付けるものです。
     * 一般的に parentId = null のものを指します。
     *
     * @return {DataTranslationMap} ルートとなるデータ変換マップ
     */
    rootDataTranslationMap() {
      return this.entities.dataTranslationMap.findOne({
        targetDataComponentModelId: this.selectedDataComponentModel.id,
        $and: [{parentId: {$exists: true}}, {parentId: null}]
      });
    },
    /**
     * データ連結モデルツリーを取得します
     */
    dataRelationModelTree() {
      const e = this.entities;
      const drmTree = new Map();
      for(const drm of this.entities.dataRelation.find()) {
        const parentId = drm.sourceDataComponentModelId;
        let children = drmTree.get(parentId);
        if(!children) {
          drmTree.set(parentId, children = []);
        }
        children.push(drm);
      }
      let order = 1;
      function buildTree(drm, ancestorDrms = []) {
        const drms = [...ancestorDrms, drm];
        const drmTargetDataComponentModel = e.dataComponentModel.findOne({id: drm.targetDataComponentModelId});
        if(!drmTargetDataComponentModel) {
          return;
        }
        const names = drms.map(ancestorDrm => {
          if(ancestorDrm.name) {
            return ancestorDrm.name;
          }
          const ancestorDrmTargetDataComponentModel = e.dataComponentModel.findOne({id: ancestorDrm.targetDataComponentModelId});
          return ancestorDrmTargetDataComponentModel.name
        });
        const node = {
          id: drms.map(drm => drm.id).join(','),
          name: names.slice().reverse().join(' < '),
          names,
          order: order++,
          indeterminate: !drmTargetDataComponentModel.dataPropertyDefinitions,
          dataRelationModel: drm,
          dataComponentModel: drmTargetDataComponentModel,
        };

        // 子階層を掘る (暫定的に10階層で打ち切り)
        const children = drms.length <= 10 ? drmTree.get(drmTargetDataComponentModel.id) : null;
        if(children) {
          node.children = children.map(child => buildTree(child, drms));
        } else {
          node.children = [];
        }
        return node;
      }
      if(this.rootDataTranslationMap) {
        return buildTree({
          id: 'root',
          name: null,
          sourceDataComponentModelId: null,
          targetDataComponentModelId: this.rootDataTranslationMap.targetDataComponentModel.id
        });
      } else {
        return null;
      }
    },
    /**
     * データ連結モデルツリーをフラットにした配列を取得します
     */
    dataRelationModelTreeFlatten() {
      const result = [];
      function flatten(node) {
        result.push(node);
        if(node.children) {
          node.children.forEach(childNode => flatten(childNode));
        }
      }
      if(this.dataRelationModelTree) {
        flatten(this.dataRelationModelTree);
      }
      return result;
    },
    /**
     * 共通辞書側のデータ項目定義の選択肢を配列で取得します。
     */
    commonDataPropertyDefinitionView() {
      return this.dataRelationModelTreeFlatten.flatMap(node => {
        return node.dataComponentModel.dataPropertyDefinitions
            .map(dpd => ({
              ...dpd,
              itemId: `${node.id}_${dpd.id}`,
              node,
              groups: [node.id],
            }));
      });
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad'
    }),
    /**
     * 共通辞書側のデータ項目定義が選択された場合に呼び出されます
     *
     * @param sourceDataPropertyDefinitionId {string} 個別辞書側のデータ項目定義ID
     * @param itemId {string|null} 共通辞書側のデータ項目定義の「アイテムID」
     * @param eo イベントオブジェクト
     */
    async handleChangeCommonDataPropertyDefinition(sourceDataPropertyDefinitionId, itemId, eo) {
      // 空選択の場合
      if(!itemId) {
        this.setDataPropertyTranslationMap(null, sourceDataPropertyDefinitionId,null);
        return;
      }

      const e = this.entities;
      const [dataRelationModelIdsStr, targetDataPropertyDefinitionId] = itemId.split('_');
      const dataRelationModelIds = dataRelationModelIdsStr.split(',');
      const targetDataPropertyDefinition = e.dataPropertyDefinition.findOne({id: targetDataPropertyDefinitionId});
      let loading = null;

      // 関連する DataTranslationMap の存在チェック＆ユーザーに確認
      if(this.getDataTranslationMapFromDataRelationModelIds(dataRelationModelIds) === null) {
        const drmTreeNode = this.dataRelationModelTreeFlatten.find(node => node.id === dataRelationModelIdsStr);
        const {value: confirm} = await Swal.fire({
          title: '共通辞書データ項目拡張の追加',
          html: [
            `データ項目定義「${targetDataPropertyDefinition.name}」を使用するには共通辞書データ項目拡張として<br>`,
            `データ構成モデルの連結<span style="white-space: pre;">「${drmTreeNode.names.join(' → ')}」</span>`,
            `を追加する必要があります。続行しますか？`
          ].join(''),
          icon: 'info',
          width: '70%',
          showCloseButton: true,
          showCancelButton: true,
          focusConfirm: true,
          confirmButtonText: "はい",
          cancelButtonText: "いいえ",
        });
        if (!confirm) {
          eo.cancel();
          return;
        }
        loading = this.$loading({
          lock: true,
          text: '共通辞書データ項目拡張を追加しています...',
          spinner: 'el-icon-loading',
        });
      }

      // ルートからデータ変換マップを辿っていく
      try {
        let lastDtmId = null;
        for (const drmId of dataRelationModelIds) {
          let dtm;
          if (drmId === 'root') {
            dtm = this.rootDataTranslationMap;
          } else {
            dtm = e.dataTranslationMap.findOne({
              targetDictionaryId: this.selectedDictionary.id,
              parentId: lastDtmId,
              dataRelationModelId: drmId
            });
            if (!dtm) {
              const drm = e.dataRelation.findOne({id: drmId});
              const drmTargetDataComponentModel = this.entities.dataComponentModel.findOne({id: drm.targetDataComponentModelId});
              dtm = await this.createDataTranslationMap({
                sourceDataComponentModelId: this.dataComponentModel.id,
                targetDataComponentModelId: drmTargetDataComponentModel.id,
                parentId: lastDtmId,
                dataRelationModelId: drmId,
                dataPropertyTranslationMaps: [],
              });
            }
          }
          lastDtmId = dtm.id;
        }
        this.setDataPropertyTranslationMap(
            lastDtmId,
            sourceDataPropertyDefinitionId,
            targetDataPropertyDefinitionId
        );
      } finally {
        if(loading) {
          loading.close();
        }
      }
    },

    /**
     * データ項目変換マップを追加します
     *
     * @param dataTranslationMapId {string|null} データ変換マップID
     * @param sourceDataPropertyDefinitionId {string} 個別辞書側のデータ項目定義ID
     * @param targetDataPropertyDefinitionId {string|null} 共通辞書側のデータ項目定義ID
     */
    setDataPropertyTranslationMap(dataTranslationMapId, sourceDataPropertyDefinitionId, targetDataPropertyDefinitionId) {
      const oldDataTranslationMaps = this.entities.dataTranslationMap.find({targetDictionaryId: this.selectedDictionary.id});
      for(const dtm of oldDataTranslationMaps) {
        this.entities.dataPropertyTranslationMap.findAndRemove({
          dataTranslationMapId: dtm.id,
          sourceDataPropertyDefinitionId: sourceDataPropertyDefinitionId
        });
      }
      if(targetDataPropertyDefinitionId) {
        this.entities.dataPropertyTranslationMap.insert({
          id: generateTemporaryID(),
          dataTranslationMapId: dataTranslationMapId,
          sourceDataPropertyDefinitionId: sourceDataPropertyDefinitionId,
          targetDataPropertyDefinitionId: targetDataPropertyDefinitionId
        });
      }
      this.updateDataPropertyDefinitionsView();
      this.$emit('dirty');
    },
    /**
     * データ項目変換マップを取得します
     *
     * @param sourceDataPropertyDefinitionId 個別辞書側のデータ項目定義ID
     * @return {null|DataPropertyDefinition} データ項目変換マップ
     */
    getDataPropertyTranslationMap(sourceDataPropertyDefinitionId) {
      const e = this.entities;
      const dataPropertyTranslationMaps = e.dataPropertyTranslationMap.find({
        sourceDataPropertyDefinitionId,
      });
      for(const dataPropertyTranslationMap of dataPropertyTranslationMaps) {
        const dataTranslationMapCount = e.dataTranslationMap.count({
          id: dataPropertyTranslationMap.dataTranslationMapId,
          targetDictionaryId: this.selectedDictionary.id
        });
        if(dataTranslationMapCount > 0) {
          return dataPropertyTranslationMap;
        }
      }
      return null;
    },
    /**
     * dataPropertyDefinitionsView を更新します
     */
    updateDataPropertyDefinitionsView() {
      if (this.isCategory2nd('common')) {
        this.dataPropertyDefinitionsView = this.dataPropertyDefinitions.map(commonDpd => ({
          target: commonDpd,
          common: commonDpd,
          local: null,
          errors: this.buildDataPropertyDefinitionError(commonDpd, commonDpd),
        }));
      } else {
        this.dataPropertyDefinitionsView = this.dataPropertyDefinitions.map(localDpd => {
          const dptm = this.getDataPropertyTranslationMap(localDpd.id);
          const commonDpdId = dptm?.targetDataPropertyDefinitionId;
          const commonDpd = commonDpdId ? this.entities.dataPropertyDefinition.findOne({id: commonDpdId}) : null;
          const commonItemId = commonDpdId ? `${this.getDataRelationModelIdsFromDataPropertyTranslationMap(dptm).join(',')}_${commonDpdId}` : null;
          return {
            target: localDpd,
            common: commonDpd || {},
            commonItemId,
            local: localDpd,
            errors: this.buildDataPropertyDefinitionError(localDpd, commonDpd),
          }
        });
      }
    },
    /**
     * DataTranslationMap を生成します
     *
     * @param dataTranslationMapAddInput {DataTranslationMap} 生成する DataTranslationMap の入力
     * @return {Promise<DataTranslationMap>} 生成された DataTranslationMap
     */
    async createDataTranslationMap(dataTranslationMapAddInput) {
      const {dataTranslationMaps} = await this.gqlMutateAndLoad({
        mutation: DataTranslationMapAddGql,
        variables: {
          addDataTranslationMaps: [dataTranslationMapAddInput]
        },
        mapping: {
          dataTranslationMap: 'dataTranslationMaps?',
          dataComponentModel: 'dataTranslationMaps.targetDataComponentModel*?',
          dataPropertyDefinition: 'dataTranslationMaps.targetDataComponentModel.dataPropertyDefinitions*?'
        }
      });
      return dataTranslationMaps[0];
    },
    /**
     * データ項目変換マップからデータ連結モデルIDの配列に変換します
     *
     * @param {DataPropertyTranslationMap} dptm データ項目変換マップ
     * @return {string[]} データ連結モデルIDの配列
     */
    getDataRelationModelIdsFromDataPropertyTranslationMap(dptm) {
      const e = this.entities;
      const drmIds = [];
      let dtm = e.dataTranslationMap.findOne({id: dptm.dataTranslationMapId});
      while(dtm && dtm.dataRelationModelId && dtm.parentId) {
        drmIds.unshift(dtm.dataRelationModelId);
        dtm = e.dataTranslationMap.findOne({id: dtm.parentId});
      }
      drmIds.unshift('root');
      return drmIds;
    },
    /**
     * データ連結モデルIDの配列からデータ変換マップを検索します
     *
     * @param drmIds {string|string[]} データ連結モデルIDの配列
     * @return {DataTranslationMap} データ変換マップ
     */
    getDataTranslationMapFromDataRelationModelIds(drmIds) {
      if(typeof drmIds === 'string') {
        drmIds = drmIds.split(',');
      }
      const e = this.entities;
      let lastDtmId = null;
      let dtm;
      for(const drmId of drmIds) {
        if(drmId === 'root') {
          dtm = this.rootDataTranslationMap;
        } else {
          dtm = e.dataTranslationMap.findOne({
            parentId: lastDtmId,
            dataRelationModelId: drmId
          });
        }
        if(dtm) {
          lastDtmId = dtm.id;
        } else {
          return null;
        }
      }
      return dtm;
    },
    buildDataPropertyDefinitionError(targetDataPropertyDefinition, commonDataPropertyDefinition) {
      const errors = [];
      if(!this.isCategory2nd('common')) {
        if(commonDataPropertyDefinition) {
          if(targetDataPropertyDefinition.dataType !== commonDataPropertyDefinition.dataType) {
            errors.push({
              level: 'warn',
              message: `個別辞書側と共通辞書側でデータ型が異なります`
            });
          }
        } else {
          errors.push({
            level: 'warn',
            message: `共通辞書「${this.selectedDictionary.name}」とのマッピングが行われていません`
          });
        }
      }
      return errors;
    },
    cellClassName({row, column}) {
      if (column.property === 'target.index' && row.target.isPrimaryKey) {
        return 'primary-key';
      } else if (column.property === 'common.index' && row.common.isPrimaryKey) {
        return 'primary-key';
      }
      return '';
    },
    toggleTableExpand(expand) {
      for (let row of this.dataPropertyDefinitionsView) {
        this.$refs.propertyTable.toggleRowExpansion(row, expand);
      }
    },
    handleChange(dataPropertyDefinition) {
      const _e = this.entities;
      _e.dataPropertyDefinition.update(dataPropertyDefinition);
      this.updateDataPropertyDefinitionsView();
      this.$emit('dirty');
    },
    handleDragEnd(e) {
      const _e = this.entities;
      const properties = this.dataPropertyDefinitions;
      const oldIdx = e.oldDraggableIndex;
      const newIdx = e.newDraggableIndex;
      if (oldIdx < newIdx) {
        for (let i = oldIdx + 1; i <= newIdx; i++) {
          const property = properties[i];
          property.index--;
          _e.dataPropertyDefinition.update(property);
        }
        const property = properties[oldIdx];
        property.index = newIdx + 1;
        _e.dataPropertyDefinition.update(property);
        this.$emit('dirty');
        this.updateDataPropertyDefinitionsView();
      } else if (newIdx < oldIdx) {
        for (let i = newIdx; i < oldIdx; i++) {
          const property = properties[i];
          property.index++;
          _e.dataPropertyDefinition.update(property);
        }
        const property = properties[oldIdx];
        property.index = newIdx + 1;
        _e.dataPropertyDefinition.update(property);
        this.$emit('dirty');
        this.updateDataPropertyDefinitionsView();
      }
    },
    openCreateDialog() {
      this.dialogCreate = {
        dataComponentModelId: this.dataComponentModel.id,
        name: '',
        description: '',
        dataType: 'string',
        isList: false,
        isRequired: false,
        isPrimaryKey: false,
        index: this.dataPropertyDefinitions
            .map(p => p.index)
            .reduce((acc, current) => Math.max(acc, current), 0) + 1,
        possibleValues: ''
      };
    },
    closeCreateDialog(apply) {
      if (apply) {
        const newEntity = this.dialogCreate;
        this.showSnackbar({
          message: `データ項目定義「${newEntity.name}」を作成中...`,
          timeout: false,
          loading: true
        });
        this.gqlMutateAndLoad({
              mutation: mutationDataPropertyDefinitionAdd,
              variables: {
                addDataPropertyDefinitions: [newEntity]
              },
              mapping: {
                dataPropertyDefinition: 'dataPropertyDefinitions',
              }
            })
            .then(_ => {
              this.showSnackbar({
                message: `データ項目定義「${newEntity.name}」を作成しました`
              });
            }, reason => {
              console.error(reason);
              this.showSnackbar({
                color: 'failed',
                message: `データ項目定義「${newEntity.name}」の作成に失敗しました`
              });
            });
      }
      this.dialogCreate = false;
    },
    openDeleteDialog(row) {
      this.dialogDelete = row;
    },
    closeDeleteDialog(apply) {
      const _e = this.entities;
      if (apply) {
        const removeEntity = this.dialogDelete;
        this.showSnackbar({
          message: `データ項目定義「${removeEntity.name}」削除中...`,
          timeout: false,
          loading: true
        });
        this.gqlMutate({
              mutation: mutationDataPropertyDefinitionRemove,
              variables: {
                removeDataPropertyDefinitionIds: [removeEntity.id]
              }
            })
            .then(data => {
              _e.dataPropertyDefinition.remove(removeEntity);

              // index を再採番する(hdsでも同様の処理が行われるので表示上のみ更新する)
              let currentIndex = 1;
              this.dataPropertyDefinitions.forEach(dpd => {
                if(dpd.id !== removeEntity.id) {
                  dpd.index = currentIndex++;
                }
              });

              this.showSnackbar({
                message: `データ項目定義「${removeEntity.name}」が削除されました`
              });
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `データ項目定義「${removeEntity.name}」の削除に失敗しました`
              });
            });
      }
      this.dialogDelete = false;
    },
  },
  watch: {
    dataPropertyDefinitionsView: {
      immediate: true,
      handler(dataPropertyDefinitionsView) {
        let level = (() => {
          if(dataPropertyDefinitionsView.length === 0) {
            return 'error';
          }
          let warn = false;
          for(const dpd of dataPropertyDefinitionsView) {
            for(const error of dpd.errors) {
              if(error.level === 'error') {
                return 'error';
              } else {
                warn = true;
              }
            }
          }
          return warn ? 'warn' : '';
        })();
        this.$emit('change-error-level', {
          type: 'dataPropertyDefinition',
          level,
        });
      }
    },
    dataPropertyDefinitions: {
      immediate: true,
      handler() {
        this.updateDataPropertyDefinitionsView();
      }
    },
    dataRelationModelTreeFlatten: {
      immediate: true,
      handler() {
        const oldCommonDataComponentModelExpanded = this.commonDataComponentModelExpanded;
        this.commonDataComponentModelExpanded = this.dataRelationModelTreeFlatten.reduce((value, node) => {
          if(oldCommonDataComponentModelExpanded[node.id] !== undefined) {
            value[node.id] = oldCommonDataComponentModelExpanded[node.id];
          } else {
            value[node.id] = this.getDataTranslationMapFromDataRelationModelIds(node.id) !== null ? undefined : false;
          }
          return value;
        }, {});
      }
    }

  }
}
</script>

<style lang="scss" scoped>
@import "src/theme";

div.body {
  display: flex;
  flex-direction: row;

  dt {
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 5px;
  }

  dd {
    font-size: 13px;
    white-space: pre-wrap;
    color: #484a4d;
  }
}

/** 共通辞書の場合、テーブルの上側のヘッダを隠す */
.hide-first-table-header-row ::v-deep .el-table__header thead tr:first-child th:first-child {
  padding: 0;

  div.cell {
    display: none;
  }
}

.data-component-model-menu-item {
  max-width: 800px;
  min-width: 500px;
}

.col.name {
  max-width: 200px;
}

::v-deep .primary-key {
  @extend .primary-key-base;
}
</style>
